import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createQuiz, listQuizzes } from '../utils/api';
import { sanitizeText, FIELD_LIMITS } from '../utils/sanitize';

function Home() {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newQuizTitle, setNewQuizTitle] = useState('');
  const [newQuizDescription, setNewQuizDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const data = await listQuizzes();
      setQuizzes(data);
    } catch (err) {
      console.error('Error fetching quizzes:', err);
      setError('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    
    const title = sanitizeText(newQuizTitle, FIELD_LIMITS.title);
    if (!title) {
      setError('Please enter a quiz title');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const result = await createQuiz({
        title: title,
        description: sanitizeText(newQuizDescription, FIELD_LIMITS.description)
      });
      
      // Navigate to the new quiz's management page
      navigate(`/quiz/${result.id}`);
    } catch (err) {
      console.error('Error creating quiz:', err);
      setError(err.message || 'Failed to create quiz');
      setCreating(false);
    }
  };

  const totalQuestions = quizzes.reduce((sum, q) => sum + (q.question_count || 0), 0);

  return (
    <div className="home-container">
      <div className="hero">
        <h1>QuizMaker</h1>
        <p>Create quizzes, share with anyone, test knowledge</p>
        <div className="hero-buttons">
          <button 
            className="btn btn-primary" 
            onClick={() => setShowCreateModal(true)}
          >
            + Create New Quiz
          </button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stats-card">
          <div className="stat">
            <span className="stat-number">{quizzes.length}</span>
            <span className="stat-label">Quizzes</span>
          </div>
        </div>
        <div className="stats-card">
          <div className="stat">
            <span className="stat-number">{totalQuestions}</span>
            <span className="stat-label">Total Questions</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading quizzes...</div>
      ) : quizzes.length > 0 ? (
        <div className="quizzes-section">
          <h3>Your Quizzes</h3>
          <div className="quizzes-list">
            {quizzes.map((quiz) => (
              <div key={quiz.id} className="quiz-list-card">
                <div className="quiz-list-info">
                  <h4>{quiz.title}</h4>
                  {quiz.description && (
                    <p className="quiz-list-description">{quiz.description}</p>
                  )}
                  <span className="quiz-list-meta">
                    {quiz.question_count} question{quiz.question_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="quiz-list-actions">
                  <button 
                    className="btn btn-secondary btn-small"
                    onClick={() => navigate(`/quiz/${quiz.id}`)}
                  >
                    Manage
                  </button>
                  <button 
                    className="btn btn-primary btn-small"
                    onClick={() => navigate(`/play/${quiz.id}`)}
                    disabled={quiz.question_count === 0}
                  >
                    Play
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <p>No quizzes yet. Create your first quiz to get started!</p>
        </div>
      )}

      {/* Create Quiz Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Quiz</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowCreateModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateQuiz}>
              <div className="form-group">
                <label>Quiz Title *</label>
                <input
                  type="text"
                  value={newQuizTitle}
                  onChange={(e) => setNewQuizTitle(e.target.value)}
                  placeholder="Enter quiz title..."
                  maxLength={FIELD_LIMITS.title}
                  autoFocus
                  required
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={newQuizDescription}
                  onChange={(e) => setNewQuizDescription(e.target.value)}
                  placeholder="Enter a description..."
                  maxLength={FIELD_LIMITS.description}
                  rows={3}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Quiz'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
